import { ModuleState } from '@gongxigames/framework/slot/fragments/module/ModuleState';
import { SystemPopup } from '@gongxigames/framework/slot/types';
import { LogicEvents } from '@gongxigames/framework/slot/services/logic/LogicEvents';
import { messages } from '@gongxigames/framework/slot';
import {
    PromotionData,
    MultiSetsMulticampaignPromotionLogic,
    MultiSetsMulticampaignPromotionStates,
    MultiSetsMulticampaignPromotionModule,
    MultiSetsMulticampaignPromotionModuleEvents,
    PromotionRegulationSettings
} from '@gongxigames/framework/promotions';

export class MultiSetsMulticampaignPromotionStartState extends ModuleState<
    null,
    MultiSetsMulticampaignPromotionModule,
    MultiSetsMulticampaignPromotionModuleEvents
> {
    protected _module!: MultiSetsMulticampaignPromotionModule;
    protected optOutPopup?: SystemPopup;
    protected infoPopup?: SystemPopup;
    protected startPopup?: SystemPopup;
    protected promotionInfoPopup?: SystemPopup;

    protected setupEvents(): void {
        this.setEventHandler(LogicEvents.BALANCE_UPDATED, this.onUpdatedBalance.bind(this));
    }

    public onEnterState(): void {
        this.emit('General.UI.DISABLE', MultiSetsMulticampaignPromotionModule.NAME);
        this.showNextPopup();
    }

    public showNextPopup(): void {
        const promotion = this.module.getPromotion();
        const isContinue = promotion.isActive();

        this.startPopup = isContinue ? this.createContinuePopup() : this.createStartPopup();

        this.emit('General.Slot.SPIN.DISABLE', MultiSetsMulticampaignPromotionModule.NAME);
        this.emit('Autoplay.FORCE_STOP');

        this.module.showPopup(this.startPopup);
    }

    public onLeaveState(): void {
        this.emit('MultiSetsMulticampaignPromotion.START_STATE_LEFT');
        this.emit('General.UI.ENABLE', MultiSetsMulticampaignPromotionModule.NAME);
        this.emit('General.Slot.SPIN.ENABLE', MultiSetsMulticampaignPromotionModule.NAME);

        if (this.module.getPromotion().isActive()) {
            this.emit('General.Promotion.Started');
        }
    }

    protected gotoInProgressState(): void {
        if (this.module.nextActionIsSpin) {
            this.module.gotoState(MultiSetsMulticampaignPromotionStates.IN_PROGRESS);
        } else {
            if (this.module.getPromotion().isLastPromotion()) {
                this.module.gotoState(MultiSetsMulticampaignPromotionStates.LAST_PROMOTION);
            } else {
                this.module.gotoState(MultiSetsMulticampaignPromotionStates.IN_PROGRESS_SPINNING);
            }
            this.module.activateCounter();
            this.module.emitHideBetPanel();
        }
    }

    private onUpdatedBalance(): void {
        const promotion = this.module.getPromotion<MultiSetsMulticampaignPromotionLogic>();
        const hasNoPromotion = !promotion.hasPromotion();
        const isStarPopupActive = this.module.hasPopupActive(this.startPopup);
        const isOptoutPopupActive = this.module.hasPopupActive(this.optOutPopup);
        const previousShownCampaingWasTerminated = hasNoPromotion && (isStarPopupActive || isOptoutPopupActive);

        if (hasNoPromotion || (promotion.isLastPromotion() && this.module.nextActionIsSpin)) {
            promotion.end();
            this.hidePopup();

            if (previousShownCampaingWasTerminated) {
                this.showWarningPopup();
            } else {
                this.module.endPromotion();
            }
        } else if (this.module.shouldTerminatePromotion()) {
            this.hidePopup();
            this.module.gotoState(MultiSetsMulticampaignPromotionStates.TERMINATED);
        }
    }

    protected onClickNoThanks(): void {
        this.hidePopup();
        const promotiondata = this.promotionData;
        const settingsPrefix = promotiondata.type === 'free-round' ? 'freeRounds' : 'awardedFeature';

        if (this.module.settings.get(`${settingsPrefix}OptOutConfirmationPopupEnabled`)) {
            this.optOutPopup = this.createOptOutPopup();
            this.module.showPopup(this.optOutPopup);
        } else {
            this.onOptOutPopupYes();
        }
    }

    protected createOptOutPopup(): SystemPopup {
        const alreadyExpired = this.module.getPromotion().isExpired();

        return this.module.popups.createMultiSetsMulticampaignOptOutPopup({
            ...this.promotionData,
            onClickNo: this.onOptOutPopupNo.bind(this),
            onClickYes: this.onOptOutPopupYes.bind(this),
            onExpiration: alreadyExpired ? () => {} : this.onExpiration.bind(this),
            regulationSettings: this.regulationSettings(),
        });
    }

    protected onOptOutPopupNo(): void {
        this.optOutPopup && this.module.hidePopup(this.optOutPopup);

        if (this.module.getPromotion().hasPromotion()) {
            this.startPopup = this.createStartPopup();
            this.module.showPopup(this.startPopup);
        } else {
            this._eventManager.emit('General.Message.SHOW', messages.PROMOTION_TERMINATED_WARNING);
        }
    }

    protected onOptOutPopupYes(): void {
        this.module.emitShowBetPanel();
        this.optOutPopup && this.module.hidePopup(this.optOutPopup);

        const onFinally = this.module.endPromotion.bind(this.module);
        this.module.getPromotion<MultiSetsMulticampaignPromotionLogic>().decline(onFinally, onFinally);
    }

    protected onExpiration(): void {
        this.hidePopup();
        this.module.emitShowBetPanel();
        this.optOutPopup && this.module.hidePopup(this.optOutPopup);

        this.showWarningPopup();
    }

    protected createStartPopup(): SystemPopup {
        const alreadyExpired = this.module.getPromotion().isExpired();

        return this.module.popups.createMultiSetsMulticampaignStartPopup({
            ...this.promotionData,
            onClickPromotionInfo: this.onClickPromotionInfo.bind(this),
            onClickPlayLater: this.onClickAskMeLater.bind(this),
            onClickAccept: this.onClickAccept.bind(this),
            onClickNoThanks: this.onClickNoThanks.bind(this),
            onExpiration: alreadyExpired ? () => {} : this.onExpiration.bind(this),
            regulationSettings: this.regulationSettings(),
        });
    }

    protected createPromotionInfoPopup(): SystemPopup {
        return this.module.popups.createMultiSetsMulticampaignPromotionWhatIsAFPopup({
            ...this.promotionData,
            onOk: this.onClickPromotionInfoOk.bind(this),
        });
    }

    protected onClickPromotionInfoOk(): void {
        this.promotionInfoPopup && this.module.hidePopup(this.promotionInfoPopup);
        this.startPopup = this.createStartPopup();
        this.module.showPopup(this.startPopup);
    }

    protected onClickPromotionInfo(): void {
        this.hidePopup();
        this.promotionInfoPopup = this.createPromotionInfoPopup();
        this.module.showPopup(this.promotionInfoPopup);
    }

    protected createInfoPopup(): SystemPopup {
        return this.module.popups.createMultiSetsMulticampaignInfo({
            ...this.promotionData,
            onOk: this.onClickInfoOk.bind(this),
        });
    }

    protected onClickInfoOk(): void {
        this.infoPopup && this.module.hidePopup(this.infoPopup);
        this.module.endPromotion();
    }

    protected createContinuePopup(): SystemPopup {
        return this.module.popups.createMultiSetsMulticampaignPromotionContinuePopup({
            ...this.promotionData,
            onClickKeepGoing: this.onClickKeepGoing.bind(this),
            onExpiration: () => {},
            regulationSettings: this.regulationSettings(),
        });
    }

    protected regulationSettings(): PromotionRegulationSettings {
        const promotiondata = this.promotionData;
        const settingsPrefix = promotiondata.type === 'free-round' ? 'freeRounds' : 'awardedFeature';

        return {
            valuedAtEnabled: this.module.settings.get(`${settingsPrefix}ValuedAtEnabled`),
            expiresInEnabled: this.module.settings.get(`${settingsPrefix}ExpiresInEnabled`),
            optOutEnabled: this.module.settings.get(`${settingsPrefix}OptOutEnabled`) && !promotiondata.isInUse,
            playLaterEnabled: this.module.settings.get(`${settingsPrefix}PlayLaterEnabled`),
            youHaveWonEnabled: this.module.settings.get(`${settingsPrefix}YouWonEnabled`)
        };
    }

    protected onClickAccept(): void {
        const onSuccess = () => {
            this.hidePopup();
            this.gotoInProgressState();
        };
        const onFailure = () => {
            this.hidePopup();
            this.module.endPromotion();
        };

        this.module.getPromotion<MultiSetsMulticampaignPromotionLogic>().accept(onSuccess, onFailure);
    }

    protected onClickKeepGoing(): void {
        this.hidePopup(); // ui allready disabled on state enter
        this.gotoInProgressState();
    }

    protected onClickAskMeLater(): void {
        const onSuccess = () => {
            this.module.emitShowBetPanel();
            this.module.gotoState(MultiSetsMulticampaignPromotionStates.DISABLED);
            this.emit('General.Promotion.Ended');
        };
        const onFailure = onSuccess;

        this.hidePopup(); // ui allready disabled on state enter
        this.module.getPromotion<MultiSetsMulticampaignPromotionLogic>().reject(onSuccess, onFailure);
    }

    protected hidePopup(): void {
        const popupToHide = this.startPopup || this.optOutPopup || this.infoPopup || this.promotionInfoPopup;
        if (popupToHide) {
            this.module.hidePopup(popupToHide);
        }
        this.module.popups.clearExpirationTimeout();
    }

    protected showWarningPopup(): void {
        const message = { ...messages.PROMOTION_TERMINATED_WARNING };

        if (message.buttons && message.buttons.length > 0) {
            message.buttons[0].actionCallback = this.module.endPromotion.bind(this.module);
        } else {
            this.module.endPromotion();
        }

        this._eventManager.emit('General.Message.SHOW', message);
    }

    protected get promotionData(): PromotionData {
        return this.module.getPromotion().getData();
    }

    protected get module(): MultiSetsMulticampaignPromotionModule {
        return this._module;
    }
}
